import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { testIds } from '@/lib/test-ids'

export type ChatContextUsage = {
  inputTokens: number
  outputTokens?: number
  contextWindow: number
  estimated: boolean
  model?: string | null
  pack?: string[]
}

type ChatContextMeterProps = {
  usage: ChatContextUsage | null
  className?: string
}

function formatTok(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`
  return String(Math.round(n))
}

/**
 * Cursor-style context window fullness ring for the chat composer.
 */
export function ChatContextMeter({ usage, className }: ChatContextMeterProps) {
  if (!usage || usage.contextWindow <= 0) return null

  const pct = Math.min(1, Math.max(0, usage.inputTokens / usage.contextWindow))
  const pctLabel = Math.round(pct * 100)
  const r = 7
  const c = 2 * Math.PI * r
  const dashOffset = c * (1 - pct)
  const warn = pct >= 0.7
  const danger = pct >= 0.9
  const stroke = danger
    ? 'stroke-red-500'
    : warn
      ? 'stroke-amber-500'
      : 'stroke-muted-foreground'

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={testIds.chatPane.contextMeter}
            className={cn(
              'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
              'text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              className,
            )}
            aria-label={`Context ${pctLabel}% full${usage.estimated ? ' (estimated)' : ''}`}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              className="-rotate-90"
              aria-hidden
            >
              <circle
                cx="9"
                cy="9"
                r={r}
                fill="none"
                className="stroke-muted"
                strokeWidth="2"
              />
              <circle
                cx="9"
                cy="9"
                r={r}
                fill="none"
                className={stroke}
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray={c}
                strokeDashoffset={dashOffset}
              />
            </svg>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[16rem] text-xs">
          <p className="font-medium">
            Context {formatTok(usage.inputTokens)} / {formatTok(usage.contextWindow)}
            {' '}({pctLabel}%)
          </p>
          <p className="text-muted-foreground mt-0.5">
            {usage.estimated
              ? 'Estimated from prompt size (provider usage not reported yet).'
              : 'From provider usage for the latest model round.'}
          </p>
          {usage.model ? (
            <p className="text-muted-foreground mt-0.5 truncate">{usage.model}</p>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
